import styles from './NewPostitionButton.module.scss';

// import { ReactComponent as AddIcon } from '@work4all/assets/icons/outline-add-24.svg';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Box, IconButton } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { SplitButton } from '@work4all/components/lib/components/split-button/SplitButton';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { CustomToolbarMappedAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { useSetting } from '@work4all/data/lib/settings/use-setting';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { EntityTableViewStack } from '../../../../../../../../../../components/EntityTableViewStack';
import { erpPinnedActions } from '../../../../../../../../../../settings/settings';
import { useShadowBzObjectApiContext } from '../../../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';
import {
  usePositionTableActions,
  UsePositionTableActionsOptions,
} from '../hooks/use-position-table-actions';
import { useWebShopActions } from '../hooks/use-web-shop-actions';
import { WebShopRequestController } from '../webshop-request-controller/WebShopRequestController';

import { PinnedItem } from './PinnedItem';
import { ShopMenu } from './ShopMenu';

type NewPostitionButtonProps = UsePositionTableActionsOptions;

export const NewPostitionButton = (props: NewPostitionButtonProps) => {
  const { disabled } = props;
  const { t } = useTranslation();

  const shadowBzObjectApi = useShadowBzObjectApiContext();
  const shadowBzObjectId = shadowBzObjectApi.initialShadowObject?.id;
  const actions = usePositionTableActions(props);

  const [webShopResultSkip, setWebShopResultSkip] = useState(true);
  const webShopActions = useWebShopActions({
    shadowBzObjectId,
    onRequestStart: () => setWebShopResultSkip(false),
  });

  const vendorActions = useMemo<CustomToolbarMappedAction>(() => {
    return {
      actionKey: 'COMMON.OCI',
      title: t('COMMON.OCI'),
      IconComponent: () => <ShoppingCartOutlinedIcon />,
      onClick: () => () => {},
      sections: webShopActions,
    } as unknown as CustomToolbarMappedAction;
  }, [webShopActions, t]);

  const options = useMemo(() => {
    const first = actions[0];
    return [
      {
        id: first.actionKey,
        name: first.title,
        icon: <AddIcon />,
        onClick: first.onClick,
      },
      ...[...actions, vendorActions].map((action) => ({
        id: action.actionKey,
        name: action.title,
        sections: action.sections,
        icon: (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="1.5rem"
          >
            <action.IconComponent style={{ color: 'var(--text03)' }} />
          </Box>
        ),
        onClick: action.onClick,
        group: [
          `${ErpPositionsKind.STANDARD}_NO_ARTICLE`,
          ErpPositionsKind.STANDARD,
          ErpPositionsKind.TEXTZEILE as string,
        ].includes(action.actionKey)
          ? ''
          : [
              ErpPositionsKind.TITEL,
              ErpPositionsKind.TITELSUMME as string,
            ].includes(action.actionKey)
          ? t('COMMON.FUNCTIONS')
          : [
              ErpPositionsKind.ZWISCHENSUMME,
              ErpPositionsKind.SEITENUMBRUCH as string,
            ].includes(action.actionKey)
          ? t('COMMON.ERP.MISCELLANEOUS')
          : t('COMMON.ERP.OCI'),
      })),
    ];
  }, [actions, vendorActions, t]);

  const settings = useSetting(erpPinnedActions());

  const dialogs = useDialogs();

  return (
    <div className={styles.buttonContainer}>
      {
        // WW-6641 Use useDialogs instead of EntityTableViewStack
      }

      <WebShopRequestController
        skip={webShopResultSkip}
        onResultCompleted={() => setWebShopResultSkip(true)}
      />
      <SplitButton
        disabled={disabled}
        itemClassName={styles.itemClassName}
        hideGroupLabels
        hideSelection
        options={options}
        firstIsTitle
        onClick={(option, event) => {
          option.onClick(event);
        }}
        variant="outlined"
        renderItem={(option, opts) => {
          if (option.id === 'COMMON.OCI') {
            return (
              <ShopMenu
                icon={option.icon}
                id={option.id}
                name={option.name}
                sections={'sections' in option ? option.sections : []}
                openTable={() => {
                  const dialog = dialogs.open(EntityTableViewStack, {
                    entity: Entities.webShopDefinition,
                    close,
                  });

                  function close() {
                    dialogs.close(dialog.id);
                  }
                }}
              />
            );
          }

          return (
            <PinnedItem
              renderPin={!(opts && opts.buttonGroup)}
              title={option.name}
              icon={option.icon}
              id={option.id}
            />
          );
        }}
      />
      <div className={styles.iconButtons}>
        {[...actions, ...webShopActions]
          .filter((x) => settings.value.includes(x.actionKey))
          .map((action) => {
            return (
              <Tooltip key={action.actionKey} title={action.title}>
                <IconButton color="primary" onClick={action.onClick}>
                  <action.IconComponent />
                </IconButton>
              </Tooltip>
            );
          })}
      </div>
    </div>
  );
};
